import * as THREE from 'three';
import { gsap } from 'gsap';

export async function mouseWebGLAction() {
  // --------- Environment detection ---------
  const ua = navigator.userAgent || '';
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) || matchMedia('(pointer: coarse)').matches;
  const isFirefox = /Firefox\//.test(ua);
  const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua);
  if (isMobile) {
    return;
  }

  // ---------------- Shader loading ----------------
  const URL = document.body.dataset.url;
  const ROOT = URL + '/public/shader';
  const shaderFiles = {
    vs: `${ROOT}/water.vert`,
    fsAdvection: `${ROOT}/fragShaderAdvection.frag`,
    fsDivergence: `${ROOT}/fragShaderDivergence.frag`,
    fsPressure: `${ROOT}/fragShaderPressure.frag`,
    fsGradientSubtract: `${ROOT}/fragShaderGradientSubtract.frag`,
    fsCurl: `${ROOT}/fragShaderCurl.frag`,
    fsVorticity: `${ROOT}/fragShaderVorticity.frag`,
    fsPoint: `${ROOT}/fragShaderPoint.frag`,
    fsDisplay: `${ROOT}/fragShaderDisplay.frag`,
  };

  const entries = Object.entries(shaderFiles);
  const texts = await Promise.all(
    entries.map(async ([key, url]) => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`Failed to load shader: ${url}`);
      const t = await r.text();
      return [key, t];
    })
  );
  const SH = Object.fromEntries(texts);

  // ---------------- Renderer & GL caps ----------------
  const canvas = document.getElementById('home-canvas');
  if (!canvas) throw new Error("Canvas element with id='canvas' not found");
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    // powerPreference: 'high-performance',
  });
  renderer.setClearColor(0x000000, 0);
  const MAX_DPR = isFirefox || isSafari ? 1.0 : 2.0;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_DPR));
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  window.addEventListener('resize', () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
  });

  const gl = renderer.getContext();
  const extHF = gl.getExtension('OES_texture_half_float');
  const extCBHF = gl.getExtension('EXT_color_buffer_half_float');
  const extLinF = gl.getExtension('OES_texture_float_linear');
  const extLinH = gl.getExtension('OES_texture_half_float_linear');

  const canHalf = !!(extHF && extCBHF);
  const texType = canHalf ? THREE.HalfFloatType : THREE.FloatType;
  const filtering = extLinF || extLinH ? THREE.LinearFilter : THREE.NearestFilter;

  // ---------------- Fullscreen pass helpers ----------------
  const quadScene = new THREE.Scene();
  const quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), null);
  quadScene.add(quad);

  function pass(material, dstRT = null) {
    quad.material = material;
    renderer.setRenderTarget(dstRT);
    renderer.render(quadScene, quadCam);
  }

  // ---------------- RenderTarget utils ----------------
  function makeRT(w, h) {
    return new THREE.WebGLRenderTarget(w, h, {
      type: texType,
      format: THREE.RGBAFormat,
      minFilter: filtering,
      magFilter: filtering,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false,
    });
  }
  class PingPong {
    constructor(w, h) {
      this.read = makeRT(w, h);
      this.write = makeRT(w, h);
    }
    swap() {
      const t = this.read;
      this.read = this.write;
      this.write = t;
    }
  }

  // ---------------- Sim params (centralized) ----------------
  const initialDyeDissipation = 0.962;
  const initialSplatRadiusBase = Math.min(0.01, innerWidth * 0.000005);
  const initialSplatForceScale = 60.0;
  const params = {
    simRes: 512,
    dtMin: 1 / 120,
    dtMax: 1 / 30,
    iterations: 4,
    viscIter: 0,
    dyeDissipation: initialDyeDissipation,
    velDissipation: 0.97,
    splatRadiusBase: initialSplatRadiusBase,
    splatForceScale: initialSplatForceScale,
    vorticityEps: 4.0,
    display: {
      useColor: 0.8,
      invert: 0.0,
      gamma: 1.0,
      threshold: 0.1,
      opacity: 1.0,
      colorFalloff: 1.0,
      gradientStrength: 0.2,
      gradientCurve: 1.2,
      gradientSaturationBoost: 0.2,
      gradientLightnessBoost: 0.85,
    },
    dyeColor: '#99f0ff',
  };

  // ブラウザ別の軽量化（Firefox/Safari）
  if (isFirefox || isSafari) {
    params.simRes = Math.min(params.simRes, 256);
  }

  const SIM_RES = params.simRes;
  const resolution = new THREE.Vector2(SIM_RES, SIM_RES);
  const texel = new THREE.Vector2(1 / SIM_RES, 1 / SIM_RES);

  let dt = 1 / 60; // 可変デルタタイム

  // ===== NEW: dye color control =====
  const COLOR_FROM = new THREE.Color('#99f0ff');
  const COLOR_TO = new THREE.Color('#f4c2f8');
  const COLOR_PERIOD = 10.0;
  const TAU = Math.PI * 2;

  const dyeColor = new THREE.Color(params.dyeColor); // default
  const dyeHighlight = new THREE.Color();

  function refreshHighlightColor() {
    const hsl = { h: 0, s: 0, l: 0 };
    dyeColor.getHSL(hsl);
    const satBoost = THREE.MathUtils.clamp(params.display.gradientSaturationBoost, 0, 1);
    const lightBoost = THREE.MathUtils.clamp(params.display.gradientLightnessBoost, 0, 1);
    const s = THREE.MathUtils.clamp(hsl.s + satBoost, 0, 1);
    const l = THREE.MathUtils.clamp(hsl.l + lightBoost, 0, 1);
    dyeHighlight.setHSL(hsl.h, s, l);
  }
  refreshHighlightColor();
  function toColor(c) {
    if (c instanceof THREE.Color) return c.clone();
    if (typeof c === 'string') return new THREE.Color(c);
    if (typeof c === 'number') return new THREE.Color(c); // 0xRRGGBB
    if (typeof c === 'object' && c) {
      let { r = 0, g = 0, b = 0 } = c;
      // ★ 255っぽい値は0–1へ正規化
      if (r > 1 || g > 1 || b > 1) {
        r /= 255;
        g /= 255;
        b /= 255;
      }
      return new THREE.Color(r, g, b);
    }
    throw new Error('Unsupported color format');
  }
  function setDyeColor(c) {
    const col = toColor(c);
    dyeColor.copy(col);
    params.dyeColor = `#${dyeColor.getHexString()}`;
    refreshHighlightColor();
    updateDisplayColors();
    console.log('[Fluid] dyeColor set to', dyeColor.getStyle());
  }
  function getDyeColor() {
    return `#${dyeColor.getHexString()}`;
  }
  // expose small API (optional)
  function setUseColor(on) {
    params.display.useColor = on ? 1.0 : 0.0;
    mDisplay.uniforms.uUseColor.value = params.display.useColor;
  }
  function setColorFalloff(power) {
    params.display.colorFalloff = Math.max(power, 0.0001);
    mDisplay.uniforms.uColorFalloff.value = params.display.colorFalloff;
  }
  function setGradientStrength(strength) {
    params.display.gradientStrength = THREE.MathUtils.clamp(strength, 0.0, 1.0);
    mDisplay.uniforms.uGradientStrength.value = params.display.gradientStrength;
  }
  function setGradientCurve(curve) {
    params.display.gradientCurve = Math.max(curve, 0.0001);
    mDisplay.uniforms.uGradientCurve.value = params.display.gradientCurve;
  }
  function setVorticityStrength(eps) {
    params.vorticityEps = eps;
    mVorticity.uniforms.uEps.value = params.vorticityEps;
  }
  function applyParams() {
    // 表示系
    mDisplay.uniforms.uInvert.value = params.display.invert;
    mDisplay.uniforms.uGamma.value = params.display.gamma;
    mDisplay.uniforms.uThreshold.value = params.display.threshold;
    mDisplay.uniforms.uOpacity.value = params.display.opacity;
    mDisplay.uniforms.uUseColor.value = params.display.useColor;
    mDisplay.uniforms.uColorFalloff.value = params.display.colorFalloff;
    mDisplay.uniforms.uGradientStrength.value = params.display.gradientStrength;
    mDisplay.uniforms.uGradientCurve.value = params.display.gradientCurve;
    refreshHighlightColor();
    updateDisplayColors();
    // 渦度
    mVorticity.uniforms.uEps.value = params.vorticityEps;
    // スプラット半径
    mPoint.uniforms.uRadius.value = params.splatRadiusBase;
  }
  window.fluid = Object.assign(window.fluid || {}, {
    setDyeColor,
    getDyeColor,
    setUseColor,
    setVorticityStrength,
    setColorFalloff,
    setGradientStrength,
    setGradientCurve,
    params,
    applyParams,
  });

  // ---------------- Allocate buffers ----------------
  const velocity = new PingPong(SIM_RES, SIM_RES);
  const dye = new PingPong(SIM_RES, SIM_RES);
  const pressure = new PingPong(SIM_RES, SIM_RES);
  const divergence = makeRT(SIM_RES, SIM_RES);

  // ---------------- Material factory ----------------
  function mat(fs, uniforms, options = {}) {
    return new THREE.RawShaderMaterial({
      vertexShader: SH.vs,
      fragmentShader: fs,
      uniforms,
      depthTest: false,
      depthWrite: false,
      transparent: false,
      ...options,
    });
  }

  // --- Advection ---
  const mAdvect = mat(SH.fsAdvection, {
    uVelocity: { value: null },
    uSource: { value: null },
    uDt: { value: dt },
    uDissipation: { value: params.velDissipation },
    uTexel: { value: texel },
    uResolution: { value: resolution },
  });

  // --- Divergence ---
  const mDivergence = mat(SH.fsDivergence, {
    uVelocity: { value: null },
    uTexel: { value: texel },
    uResolution: { value: resolution },
  });

  // --- Pressure (Jacobi) ---
  const mPressure = mat(SH.fsPressure, {
    uPressure: { value: null },
    uDivergence: { value: null },
    uTexel: { value: texel },
    uResolution: { value: resolution },
  });

  // --- Gradient Subtraction ---
  const mGradSub = mat(SH.fsGradientSubtract, {
    uVelocity: { value: null },
    uPressure: { value: null },
    uTexel: { value: texel },
    uResolution: { value: resolution },
  });

  // --- Curl (渦度) & Vorticity confinement ---
  const mCurl = mat(SH.fsCurl, {
    uVelocity: { value: null },
    uTexel: { value: texel },
  });

  const mVorticity = mat(SH.fsVorticity, {
    uVelocity: { value: null },
    uCurl: { value: null },
    uTexel: { value: texel },
    uEps: { value: params.vorticityEps },
    uDt: { value: dt },
  });

  // --- Point splat ---
  const mPoint = mat(SH.fsPoint, {
    uBuffer: { value: null },
    uPoint: { value: new THREE.Vector2() },
    uColor: { value: new THREE.Vector3() },
    uRadius: { value: params.splatRadiusBase },
  });

  // --- Display ---
  const mDisplay = mat(
    SH.fsDisplay,
    {
      uTexture: { value: null },
      uResolution: { value: resolution },
      uInvert: { value: params.display.invert },
      uGamma: { value: params.display.gamma },
      uThreshold: { value: params.display.threshold },
      uOpacity: { value: params.display.opacity },
      uUseColor: { value: params.display.useColor },
      uDyeColor: { value: new THREE.Vector3(dyeColor.r, dyeColor.g, dyeColor.b) },
      uHighlightColor: { value: new THREE.Vector3(dyeHighlight.r, dyeHighlight.g, dyeHighlight.b) },
      uColorFalloff: { value: params.display.colorFalloff },
      uGradientStrength: { value: params.display.gradientStrength },
      uGradientCurve: { value: params.display.gradientCurve },
    },
    {
      transparent: true,
    }
  );

  function updateDisplayColors() {
    const dyeVec = mDisplay.uniforms.uDyeColor.value;
    const highlightVec = mDisplay.uniforms.uHighlightColor.value;
    dyeVec.set(dyeColor.r, dyeColor.g, dyeColor.b);
    highlightVec.set(dyeHighlight.r, dyeHighlight.g, dyeHighlight.b);
  }

  function pingPong01(x) {
    const f = x - Math.floor(x);
    return 0.5 - 0.5 * Math.cos(TAU * f);
  }

  function updateAnimatedDyeColor(elapsedSec) {
    const mix = pingPong01(elapsedSec / COLOR_PERIOD);
    dyeColor.lerpColors(COLOR_FROM, COLOR_TO, mix);
    refreshHighlightColor();
    updateDisplayColors();
    params.dyeColor = `#${dyeColor.getHexString()}`;
  }

  updateDisplayColors();

  // ---------------- Helpers ----------------
  function splat(rt, point, colorVec3, radius) {
    mPoint.uniforms.uBuffer.value = rt.read.texture;
    mPoint.uniforms.uPoint.value.set(point.x, point.y);
    mPoint.uniforms.uColor.value.set(colorVec3.x, colorVec3.y, colorVec3.z);
    mPoint.uniforms.uRadius.value = radius;
    pass(mPoint, rt.write);
    rt.swap();
  }

  // ---------------- Mouse interaction (move-only) ----------------
  let lastX = 0,
    lastY = 0,
    hasPrev = false;

  window.addEventListener('pointerenter', (e) => {
    const rect = canvas.getBoundingClientRect();
    lastX = (e.clientX - rect.left) / rect.width;
    lastY = 1 - (e.clientY - rect.top) / rect.height;
    hasPrev = false;
  });

  window.addEventListener('pointerleave', () => {
    hasPrev = false;
  });

  window.addEventListener('pointermove', (e) => updateMouse(e));

  function updateMouse(e) {
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = 1 - (e.clientY - rect.top) / rect.height;
    let dx = 0.0,
      dy = 0.0;
    if (hasPrev) {
      dx = x - lastX;
      dy = y - lastY;
    }
    lastX = x;
    lastY = y;
    hasPrev = true;

    // 小さすぎる移動は無視（ノイズ防止）
    const movement = Math.hypot(dx, dy);
    if (movement < 0.0003) return;

    const speed = Math.min(1.0, movement * 20.0);
    const r = params.splatRadiusBase + 0.03 * speed;

    // 速度注入
    splat(velocity, new THREE.Vector2(x, y), new THREE.Vector3(dx * params.splatForceScale, dy * params.splatForceScale, 0.0), r);

    // 染料注入（現在の dyeColor を使用）
    splat(dye, new THREE.Vector2(x, y), new THREE.Vector3(dyeColor.r, dyeColor.g, dyeColor.b), r * 1.05);
  }

  //a, buttonで大きさ小さく
  function cancelEl() {
    const targets = document.querySelectorAll('a, button');
    if (targets.length === 0) return;
    targets.forEach((target) => {
      target.addEventListener('pointerenter', () => {
        gsap.to(params, {
          splatRadiusBase: 0.0,
          splatForceScale: 0.0,
          dyeDissipation: 0.0,
          duration: 1.6,
          overwrite: 'auto',
        });
      });
      target.addEventListener('pointerleave', () => {
        gsap.to(params, {
          dyeDissipation: initialDyeDissipation,
          splatRadiusBase: initialSplatRadiusBase,
          splatForceScale: initialSplatForceScale,
          duration: 0.4,
          overwrite: 'auto',
        });
      });
    });
  }
  cancelEl();

  // ---------------- Simulation step ----------------
  function step() {
    // 1) Advect velocity
    mAdvect.uniforms.uVelocity.value = velocity.read.texture;
    mAdvect.uniforms.uSource.value = velocity.read.texture;
    mAdvect.uniforms.uDissipation.value = params.velDissipation;
    mAdvect.uniforms.uDt.value = dt;
    pass(mAdvect, velocity.write);
    velocity.swap();

    // 1.5) Vorticity confinement（渦度の保存力で小渦を強調）
    mCurl.uniforms.uVelocity.value = velocity.read.texture;
    pass(mCurl, divergence); // 一時バッファとして divergence RT を流用（Rにcurl）

    mVorticity.uniforms.uVelocity.value = velocity.read.texture;
    mVorticity.uniforms.uCurl.value = divergence.texture;
    mVorticity.uniforms.uDt.value = dt;
    pass(mVorticity, velocity.write);
    velocity.swap();

    // 2) [optional] velocity diffusion
    for (let i = 0; i < params.viscIter; i++) {
      /* optional */
    }

    // 3) Divergence
    mDivergence.uniforms.uVelocity.value = velocity.read.texture;
    pass(mDivergence, divergence);

    // 4) Pressure solve (Jacobi)
    for (let i = 0; i < params.iterations; i++) {
      mPressure.uniforms.uPressure.value = pressure.read.texture;
      mPressure.uniforms.uDivergence.value = divergence.texture;
      pass(mPressure, pressure.write);
      pressure.swap();
    }

    // 5) Subtract gradient
    mGradSub.uniforms.uVelocity.value = velocity.read.texture;
    mGradSub.uniforms.uPressure.value = pressure.read.texture;
    pass(mGradSub, velocity.write);
    velocity.swap();

    // 6) Advect dye
    mAdvect.uniforms.uVelocity.value = velocity.read.texture;
    mAdvect.uniforms.uSource.value = dye.read.texture;
    mAdvect.uniforms.uDissipation.value = params.dyeDissipation;
    mAdvect.uniforms.uDt.value = dt;
    pass(mAdvect, dye.write);
    dye.swap();
  }

  // ---------------- Render ----------------
  function renderDisplay() {
    mDisplay.uniforms.uTexture.value = dye.read.texture;
    pass(mDisplay, null);
  }

  // ---------------- Loop ----------------
  let lastT = performance.now();
  const startTime = lastT;
  function loop() {
    const now = performance.now();
    dt = THREE.MathUtils.clamp((now - lastT) / 1000, params.dtMin, params.dtMax);
    lastT = now;
    updateAnimatedDyeColor((now - startTime) / 1000);
    step();
    renderDisplay();
    requestAnimationFrame(loop);
  }
  loop();
}
